import { useModal } from "@/app/shared/modal-views/use-modal";
import { useMemo, useState } from "react";
import { PiUserPlus, PiXBold } from "react-icons/pi";
import { ActionIcon, Button, Checkbox, Input, Title } from "rizzui";
import { assignRole } from "@/server/api-services";
import { User } from "@/types";
import toast from "react-hot-toast";


export default function AssignRole({
    users,
    roleId,
    onAssigned,
}: {
    users: User[];
    roleId: string;
    onAssigned: () => void;
}) {
    const { closeModal } = useModal();
    const [search, setSearch] = useState("");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [isLoading, setLoading] = useState(false);

    const filteredUsers = useMemo(() => {
        const query = search.trim().toLowerCase();

        if (!query) {
            return users;
        }

        return users.filter(
            (user) =>
                user.fullname.toLowerCase().includes(query) ||
                user.email.toLowerCase().includes(query),
        );
    }, [search, users]);

    const allFilteredSelected =
        filteredUsers.length > 0 &&
        filteredUsers.every((user) => selectedIds.includes(user.id));

    const toggleUserSelection = (userId: string) => {
        setSelectedIds((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId],
        );
    };

    const toggleSelectAllFiltered = () => {
        if (allFilteredSelected) {
            const filteredIds = filteredUsers.map((user) => user.id);
            setSelectedIds((prev) =>
                prev.filter((id) => !filteredIds.includes(id)),
            );
            return;
        }

        const merged = new Set(selectedIds);
        filteredUsers.forEach((user) => merged.add(user.id));
        setSelectedIds(Array.from(merged));
    };

    const handleAssignSelected = async () => {
        if (!selectedIds.length) {
            toast.error("Select at least one user.");
            return;
        }

        setLoading(true);
        try {
            const failed: string[] = [];

            for (const userId of selectedIds) {
                const response = await assignRole(userId, roleId);
                if (response.statusCode !== 200) {
                    failed.push(userId);
                }
            }

            if (failed.length) {
                toast.error(`Assigned ${selectedIds.length - failed.length} of ${selectedIds.length} users.`);
            } else {
                toast.success(`Assigned ${selectedIds.length} user${selectedIds.length > 1 ? "s" : ""}.`);
            }

            onAssigned();
            closeModal();
        } catch (error) {
            console.error("Failed to assign role", error);
            toast.error("Failed to assign selected users.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid grid-cols-1 gap-4 p-6 @container [&_.rizzui-input-label]:font-medium [&_.rizzui-input-label]:text-gray-900">
            <div className="col-span-full flex items-center justify-between">
                <Title as="h4" className="font-semibold">
                    Assign Users To Role
                </Title>
                <ActionIcon size="sm" variant="text" onClick={closeModal}>
                    <PiXBold className="h-auto w-5" />
                </ActionIcon>
            </div>

            {users.length === 0 ? (
                <div className="rounded-md border border-dashed border-muted p-4 text-sm text-gray-600">
                    All users are already assigned to this role.
                </div>
            ) : (
                <>
                    <Input
                        type="search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Search users by name or email"
                    />

                    <div className="flex items-center justify-between rounded-md bg-gray-100 px-3 py-2">
                        <Checkbox
                            label="Select all visible"
                            checked={allFilteredSelected}
                            onChange={toggleSelectAllFiltered}
                        />
                        <span className="text-xs text-gray-500">
                            {selectedIds.length} selected
                        </span>
                    </div>

                    <div className="max-h-72 space-y-2 overflow-y-auto rounded-md border border-muted p-2">
                        {filteredUsers.map((user) => (
                            <label
                                key={user.id}
                                className="flex cursor-pointer items-center justify-between rounded-md px-2 py-2 hover:bg-gray-50"
                            >
                                <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-gray-900">
                                        {user.fullname}
                                    </p>
                                    <p className="truncate text-xs text-gray-500">
                                        {user.email}
                                    </p>
                                </div>
                                <Checkbox
                                    checked={selectedIds.includes(user.id)}
                                    onChange={() => toggleUserSelection(user.id)}
                                />
                            </label>
                        ))}

                        {filteredUsers.length === 0 && (
                            <p className="px-2 py-4 text-center text-sm text-gray-500">
                                No users match your search.
                            </p>
                        )}
                    </div>
                </>
            )}

            <div className="col-span-full flex items-center justify-end gap-4">
                <Button
                    variant="outline"
                    onClick={closeModal}
                    className="w-full @xl:w-auto"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleAssignSelected}
                    isLoading={isLoading}
                    disabled={users.length === 0 || selectedIds.length === 0}
                    className="w-full @xl:w-auto"
                >
                    <PiUserPlus className="me-2 h-4 w-4" />
                    Assign Selected
                </Button>
            </div>
        </div>
    );
}
